"use client";

import { useCallback, useEffect, useState } from "react";
import { useSubscriptionCheck } from "./use-subscription-check";
import type {
  UseSubscriptionGateOptions,
  UseSubscriptionGateReturn,
} from "./types";
import { SubscriptionGateModal } from "./components/subscription-gate-modal";

/**
 * Hook to manage subscription gating from any page
 *
 * Provides subscription status and a modal for collecting payment.
 * The return URL is automatically set to the current page with query params preserved.
 *
 * @param options - Configuration options
 * @param options.requireSubscription - If true, show the paywall modal immediately when no subscription detected
 * @param options.onDismiss - Callback when user dismisses the gate without subscribing
 * @param options.onSubscribed - Callback when subscription becomes active (e.g., after returning from payment)
 *
 * @returns Hook return value with subscription status and gate controls
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const { hasSubscription, isLoading, renderGateModal } = useSubscriptionGate({
 *     requireSubscription: true,
 *   });
 *
 *   if (isLoading) return <Loading />;
 *
 *   return (
 *     <>
 *       <PageContent />
 *       {renderGateModal()}
 *     </>
 *   );
 * }
 * ```
 */
export function useSubscriptionGate(
  options: UseSubscriptionGateOptions = {}
): UseSubscriptionGateReturn {
  const { requireSubscription = false, onDismiss, onSubscribed } = options;

  // Subscription check via SWR
  const { hasActiveSubscription, subscriptionState, isLoading } =
    useSubscriptionCheck();

  // Modal state
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [hasShownGate, setHasShownGate] = useState(false);

  // Show gate when subscription is required but missing
  useEffect(() => {
    if (
      requireSubscription &&
      !isLoading &&
      !hasActiveSubscription &&
      !hasShownGate
    ) {
      setIsGateOpen(true);
      setHasShownGate(true);
    }
  }, [requireSubscription, isLoading, hasActiveSubscription, hasShownGate]);

  // Detect when subscription becomes active — if gate was shown (meaning
  // no subscription) and now hasActiveSubscription is true, the user just subscribed.
  useEffect(() => {
    if (hasShownGate && hasActiveSubscription) {
      setHasShownGate(false);
      onSubscribed?.();
    }
  }, [hasShownGate, hasActiveSubscription, onSubscribed]);

  const openGate = useCallback(() => {
    setIsGateOpen(true);
  }, []);

  const closeGate = useCallback(() => {
    setIsGateOpen(false);
    onDismiss?.();
  }, [onDismiss]);

  const renderGateModal = useCallback(
    () => <SubscriptionGateModal isOpen={isGateOpen} onClose={closeGate} />,
    [isGateOpen, closeGate]
  );

  return {
    hasSubscription: hasActiveSubscription,
    subscriptionState,
    isLoading,
    openGate,
    closeGate,
    isGateOpen,
    renderGateModal,
  };
}
